import { useTranslation } from 'react-i18next';
import {
    Box,
    HStack,
    VStack,
    Text,
    Select,
    Button,
    SimpleGrid,
} from '@chakra-ui/react';
import { useAppStore } from '../../state/store';
import { ManageDialogFrame, ManageDialogCloseFooter } from './ManageDialogFrame';
import { useManageSettingsStore, UnitSymbol, UnitSettings } from '../../state/useManageSettingsStore';

const UNIT_OPTIONS: Record<keyof UnitSettings, { value: UnitSymbol; labelKey: string }[]> = {
    length: [
        { value: 'mm', labelKey: 'manage.millimeters' },
        { value: 'cm', labelKey: 'manage.centimeters' },
        { value: 'm', labelKey: 'manage.meters' },
        { value: 'ft', labelKey: 'manage.feet' },
        { value: 'in', labelKey: 'manage.inches' },
    ],
    area: [
        { value: 'm2', labelKey: 'manage.squareMeters' },
        { value: 'ft2', labelKey: 'manage.squareFeet' },
    ],
    volume: [
        { value: 'm3', labelKey: 'manage.cubicMeters' },
        { value: 'ft3', labelKey: 'manage.cubicFeet' },
    ],
    angle: [
        { value: 'deg', labelKey: 'manage.decimalDegrees' },
        { value: 'grad', labelKey: 'manage.gradians' },
        { value: 'rad', labelKey: 'manage.radians' },
    ],
};

const KIND_LABELS: Record<keyof UnitSettings, string> = {
    length: 'manage.length',
    area: 'manage.area',
    volume: 'manage.volume',
    angle: 'manage.angle',
};

export function ProjectUnitsDialog() {
    const { t } = useTranslation();
    const isOpen = useAppStore((state) => state.manageDialog === 'units');
    const onClose = useAppStore((state) => state.closeManageDialog);
    const units = useManageSettingsStore((state) => state.units);
    const setUnit = useManageSettingsStore((state) => state.setUnit);
    const resetUnits = useManageSettingsStore((state) =>state.resetUnits); const kinds = Object.keys(UNIT_OPTIONS) as (keyof UnitSettings)[]; return (<ManageDialogFrame
            title={t('manage.projectUnits')}
            isOpen={isOpen}
            onClose={onClose}
            footer={
                <ManageDialogCloseFooter
                    onClose={onClose}
                    extra={
                        <Button variant="ghost" size="sm" onClick={resetUnits} color="var(--color-text-secondary)">{t('manage.restoreDefaults')}</Button>
                    }
                />
            }
        >
            <VStack spacing={4} align="stretch">
                {kinds.map((kind) => (
                    <HStack key={kind} justify="space-between">
                        <Text fontSize="sm" color="var(--color-text-secondary)" whiteSpace="nowrap">
                            {t(KIND_LABELS[kind] as any)}
                        </Text>
                        <Select
                            size="sm"
                            width="220px"
                            value={units[kind]}
                            onChange={(e) => setUnit(kind, e.target.value as UnitSymbol)}
                            bg="var(--color-bg-tertiary)"
                            borderColor="var(--color-border)"
                        >
                            {UNIT_OPTIONS[kind].map((opt) => (
                                <option key={opt.value} value={opt.value}>
                                    {t(opt.labelKey as any)}
                                </option>
                            ))}
                        </Select>
                    </HStack>
                ))}
                <Box pt={2}>
                    <Text fontSize="xs" color="var(--color-text-secondary)">
                        Format example: 1000.00 {units.length === 'mm' ? 'mm' : units.length}
                    </Text>
                </Box>
                <SimpleGrid columns={2} spacing={2} pt={1}>
                    <Text fontSize="xs" color="var(--color-text-secondary)">
                        Area unit: {units.area}
                    </Text>
                    <Text fontSize="xs" color="var(--color-text-secondary)">
                        Volume unit: {units.volume}
                    </Text>
                </SimpleGrid>
            </VStack>
        </ManageDialogFrame>
    );
}
